import React from 'react';
import clsx from 'clsx';
import { Trash2, ArrowRight, Shield } from 'lucide-react';
import { PlaybookTransformItem, PlaybookTransformType, PlaybookExpression, PlaybookSpreading, PlaybookEventConfig } from '@/types/playbook';
import { UnifiedSchema, EventSchema } from '@/types/schema';
import { getTypeColorClass } from '@/utils/schemaUtils';
import EventUsageBar from '@/components/schema/EventUsageBar';
import PlaybookTransformEditor from '@/components/playbooks/transforms-editor/PlaybookTransformEditor';
import { EditorSelection } from '@/components/playbooks/PlaybookEditorTab'; // Import EditorSelection

interface PlaybookTransformCardProps {
  transform: PlaybookTransformItem;
  isSelected: boolean;
  onTransformSelect: (transformId: string) => void;
  onDeleteTransform: (transformId: string) => void;
  onUpdateTransform: (transformId: string, updates: Partial<PlaybookTransformItem>) => void;
  onSelectionChange: (selection: EditorSelection) => void;
  inputSchema: UnifiedSchema | null;
  outputSchema: UnifiedSchema | null;
  allEvents: EventSchema[];
  uiEventConfigs: PlaybookEventConfig[];
  allUiTransforms: PlaybookTransformItem[];
  allowUndefinedTransforms: boolean;
  
  // Helper functions passed as props
  getTransformDisplayName: (type: PlaybookTransformType) => string;
  getTransformDetailDisplay: (transform: PlaybookTransformItem) => string;
  getMappingTypeDisplay: (transform: PlaybookTransformItem) => JSX.Element | null;
  getTypeIcon: (dataType?: string) => JSX.Element;
  truncatePath: (path: string, maxLength?: number) => string;
  isPropertyPairApplicableToAllEvents: (inputPath: string | undefined, outputPath: string | undefined, allEvents: EventSchema[]) => boolean; // Update this prop
}

const PlaybookTransformCard: React.FC<PlaybookTransformCardProps> = ({
  transform,
  isSelected,
  onTransformSelect,
  onDeleteTransform,
  onUpdateTransform,
  onSelectionChange,
  inputSchema,
  outputSchema,
  allEvents,
  uiEventConfigs,
  allUiTransforms,
  allowUndefinedTransforms,
  getTransformDisplayName,
  getTransformDetailDisplay,
  getMappingTypeDisplay,
  getTypeIcon,
  truncatePath,
  isPropertyPairApplicableToAllEvents, // Destructure this prop
}) => {
  return (
    <div className="flex flex-col">
      <div
        className={clsx(
          "flex items-center p-3 rounded-lg border transition-colors cursor-pointer relative group",
          isSelected
            ? "border-blue-200 dark:border-blue-900 dark:bg-gray-800 bg-white"
            : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md hover:border-gray-300 hover:dark:border-gray-600"
        )}
        onClick={() => onTransformSelect(transform.id)}
      >
        {/* Input Key */}
        <div className="flex items-center gap-2 w-[25%]">
          {transform.type === 'enrichment' ? (
            // For enrichment, show "No input" text
            <span className="text-sm text-gray-500 dark:text-gray-400">
              No input
            </span>
          ) : transform.inputPath ? (
            // For other transforms with input path
            <>
              {transform.inputType && typeof transform.inputType === 'string' && transform.inputType.length > 0 && (
                <span className={`inline-flex w-6 h-6 justify-center items-center rounded-full ${getTypeColorClass(transform.inputType)}`}>
                  {getTypeIcon(transform.inputType)}
                </span>
              )}
              <span className="font-medium text-sm text-gray-900 dark:text-white truncate" title={transform.inputPath}>
                {truncatePath(transform.inputPath)}
              </span>
            </>
          ) : (
            // For transforms without input path, show appropriate content
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {transform.type === 'expression' ? 'Input' :
              transform.type === 'filter' ? 'Event filter' :
              'No input'}
            </span>
          )}
        </div>

        {/* Transform Type and Details - Combined in pill */}
        <div className="flex items-center gap-2 w-[25%]">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getTypeColorClass(transform.type)}`}>
            {getTransformDisplayName(transform.type)}{getTransformDetailDisplay(transform)}
          </span>
        </div>

        {/* Output Key */}
        <div className="flex items-center gap-2 w-[25%]">
          {transform.type !== 'filter' && (
            <ArrowRight size={16} className="text-gray-400 flex-shrink-0" />
          )}
          {transform.outputPath ? (
            <>
              {transform.outputType && typeof transform.outputType === 'string' && transform.outputType.length > 0 && (
                <span className={`inline-flex w-6 h-6 justify-center items-center rounded-full ${getTypeColorClass(transform.outputType)}`}>
                  {getTypeIcon(transform.outputType)}
                </span>
              )}
              <span className="text-sm font-medium text-gray-900 dark:text-white truncate" title={transform.outputPath}>
                {truncatePath(transform.outputPath)}
              </span>
            </>
          ) : transform.type === 'spreading' ? (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {transform.content && (transform.content as PlaybookSpreading).inferredOutputProperties?.length
                ? `${(transform.content as PlaybookSpreading).inferredOutputProperties!.length} properties`
                : 'Series of properties'}
            </span>
          ) : transform.type === 'expression' ? (
            transform.content && (transform.content as PlaybookExpression).declaredOutputProperties?.length === 1 ? (
              <>
                {(transform.content as PlaybookExpression).declaredOutputProperties![0].dataType && (
                  <span className={`inline-flex w-6 h-6 justify-center items-center rounded-full ${getTypeColorClass((transform.content as PlaybookExpression).declaredOutputProperties![0].dataType)}`}>
                    {getTypeIcon((transform.content as PlaybookExpression).declaredOutputProperties![0].dataType)}
                  </span>
                )}
                <span className="text-sm font-medium text-gray-900 dark:text-white truncate" title={(transform.content as PlaybookExpression).declaredOutputProperties![0].path}>
                  {truncatePath((transform.content as PlaybookExpression).declaredOutputProperties![0].path)}
                </span>
              </>
            ) : (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {transform.content && (transform.content as PlaybookExpression).declaredOutputProperties?.length
                  ? `${(transform.content as PlaybookExpression).declaredOutputProperties!.length} outputs`
                  : 'Dynamic outputs'}
              </span>
            )
          ) : transform.type === 'filter' ? (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Filters events
            </span>
          ) : transform.type === 'mapping' && !transform.outputPath ? (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              No output
            </span>
          ) : null}
        </div>

        {/* Additional Attributes */}
        <div className="flex items-center gap-2 w-[15%]">
          {transform.isPII && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900 dark:text-fuchsia-200 whitespace-nowrap">
              <Shield size={12} className="mr-1" /> PII
            </span>
          )}
          {transform.mappingType && getMappingTypeDisplay(transform)}
        </div>

        {/* Event Usage Bar and Actions */}
        <div className="flex items-center justify-end gap-2 w-[10%]">
          {transform.eventUsageCount !== undefined && transform.totalEventCount !== undefined && transform.eventNames && (
            <EventUsageBar 
              usedCount={transform.eventUsageCount} 
              totalCount={transform.totalEventCount} 
              eventNames={transform.eventNames} 
              isGlobalTransform={transform.appliesToScope === 'global'} // Pass new prop
            />
          )}
          <div className={clsx(
            "flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity",
            isSelected && 'opacity-100'
          )}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteTransform(transform.id);
              }}
              className="text-gray-600 hover:text-red-600 hover:bg-red-100 dark:text-gray-300 dark:hover:bg-red-900/50 dark:hover:text-red-400 p-1 rounded-md"
              title="Delete Transform"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      {isSelected && (
        <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 pb-1">
          <PlaybookTransformEditor
            selectedTransform={transform}
            onUpdateTransform={onUpdateTransform}
            inputSchema={inputSchema}
            outputSchema={outputSchema}
            allEvents={allEvents}
            uiEventConfigs={uiEventConfigs}
            onTransformSelect={() => onTransformSelect('')} // Deselect when editor is closed
            onSelectionChange={onSelectionChange}
            allUiTransforms={allUiTransforms}
            getUsedInputProperties={() => []} // Placeholder, actual logic in PlaybookAIMappingControls
            getUsedOutputProperties={() => []} // Placeholder, actual logic in PlaybookAIMappingControls
            allowUndefinedTransforms={allowUndefinedTransforms}
            isPropertyPairApplicableToAllEvents={(inputPath, outputPath) => isPropertyPairApplicableToAllEvents(inputPath, outputPath, allEvents)} // Pass this prop
          />
        </div>
      )}
    </div>
  );
};

export default PlaybookTransformCard;
